import { useTranslation } from 'react-i18next';
import type { UseBonusesResult } from '@/api';
import type { BonusBucket, BonusModuleFilter, BonusTransaction } from '@/domain/bonuses';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { EmptyState } from '../common/empty-state';
import { Spinner } from '../common/spinner';
import { Toast } from '../common/toast';
import { BonusListRow } from './BonusListRow';

export interface BonusesPanelProps {
  bonuses: UseBonusesResult;
  onSelectBonus?: (bonus: BonusTransaction) => void;
  className?: string;
}

const BUCKET_CHIPS: {
  value: BonusBucket;
  labelKey: string;
  defaultLabel: string;
}[] = [
  { value: 'active', labelKey: 'Active', defaultLabel: 'Active' },
  { value: 'history', labelKey: 'History', defaultLabel: 'History' },
];

const MODULE_CHIPS: {
  value: BonusModuleFilter;
  labelKey: string;
  defaultLabel: string;
}[] = [
  { value: 'all', labelKey: 'All', defaultLabel: 'All' },
  { value: 'sport', labelKey: 'Sport', defaultLabel: 'Sport' },
  { value: 'casino', labelKey: 'Casino', defaultLabel: 'Casino' },
];

function FilterChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'rounded-md px-3 py-2 text-[13px] whitespace-nowrap transition-colors',
        selected
          ? 'bg-action-primary font-bold text-text-on-action'
          : 'bg-bg-elevated font-normal text-text-secondary hover:text-text-primary',
      )}
    >
      {label}
    </button>
  );
}

/**
 * Profile bonuses body: Active/History + All/Sport/Casino chips and list rows.
 */
export function BonusesPanel({ bonuses, onSelectBonus, className }: BonusesPanelProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const {
    bucket,
    setBucket,
    module,
    setModule,
    rows,
    isLoading,
    listError,
    currency,
  } = bonuses;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div
        role="group"
        aria-label={label('Bonus status', 'Bonus status')}
        className="flex flex-wrap gap-2"
      >
        {BUCKET_CHIPS.map((chip) => (
          <FilterChip
            key={chip.value}
            label={label(chip.labelKey, chip.defaultLabel)}
            selected={bucket === chip.value}
            onClick={() => setBucket(chip.value)}
          />
        ))}
      </div>

      <div
        role="group"
        aria-label={label('Bonus type', 'Bonus type')}
        className="flex flex-wrap gap-2"
      >
        {MODULE_CHIPS.map((chip) => (
          <FilterChip
            key={chip.value}
            label={label(chip.labelKey, chip.defaultLabel)}
            selected={module === chip.value}
            onClick={() => setModule(chip.value)}
          />
        ))}
      </div>

      {listError ? (
        <Toast tone="danger">
          {label('Could not load bonuses', 'Could not load bonuses')}
          {listError.message ? `: ${listError.message}` : ''}
        </Toast>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={label('Loading bonuses', 'Loading bonuses')} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="bonuses"
          title={label('No bonuses', 'No bonuses')}
          description={
            bucket === 'active'
              ? label(
                  'You have no active offers right now.',
                  'You have no active offers right now.',
                )
              : label(
                  'No bonus history for this filter.',
                  'No bonus history for this filter.',
                )
          }
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((bonus) => (
            <li key={`${bonus.Type}-${bonus.Id}`}>
              <BonusListRow
                bonus={bonus}
                currency={currency}
                onSelect={onSelectBonus}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
